import type { FieldDefinition } from '@/api/types'
import { formatShortDate } from '@/lib/dealFormat'

/**
 * Formats a custom field value for display in a card line or table cell.
 * Returns null when there is no value (the caller renders a placeholder / skips the line).
 */
export function formatCustomFieldValue(def: FieldDefinition, raw: unknown): string | null {
  if (raw == null || raw === '') return null
  if (def.dataType === 'boolean') return raw === true ? 'Yes' : 'No'
  if (def.dataType === 'date') {
    // A value mistyped as a date (e.g. field type changed from text) must not render "Invalid Date".
    const s = String(raw)
    return Number.isNaN(new Date(s).getTime()) ? s : formatShortDate(s)
  }
  if (def.dataType === 'datetime') {
    // Stored as a 'YYYY-MM-DDTHH:mm' local instant; show the date with its time of day.
    const s = String(raw)
    const d = new Date(s)
    return Number.isNaN(d.getTime())
      ? s
      : `${formatShortDate(s)}, ${d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' })}`
  }
  if (def.dataType === 'file') {
    return raw && typeof raw === 'object'
      ? ((raw as { filename?: string }).filename ?? 'File')
      : null
  }
  if (Array.isArray(raw)) return raw.length > 0 ? raw.join(', ') : null
  return String(raw)
}
